import { countries, languages, localeDict } from '@/consts/languageAccordance';
import { sortByArray } from '../fp';
import { config } from '@/config';
import * as Sentry from '@sentry/nextjs';

type CustomLocale = {
    language: {
        localeName: string;
        id: number;
        name: Record<string, string>;
        code: string;
    };
    country: {
        localeName: string;
        code: string;
        code3: string;
        name: Record<string, string>;
        phraseId: number;
    };

    languageCode: string;
    countryCode: string;
};

const { defaultLocale, locales } = config;

const getCountryCode = (localeString: string): string => {
    const countryCode = localeString.split('-')[1];
    return countryCode?.trim().toLowerCase();
};

const getLanguageCode = (localeString: string): string => {
    const languageCode = localeString.split('-')[0];
    return languageCode.trim();
};

export const checkLocaleExists = (localeString: string): boolean =>
    localeDict[localeString] !== undefined;

export const checkLocaleIsValid = (localeString: string): boolean => {
    if (!locales.includes(localeString)) {
        return false;
    }
    const languageCode = getLanguageCode(localeString);
    const countryCode = getCountryCode(localeString);
    return languages[languageCode] !== undefined && countries[countryCode] !== undefined;
};

export const getLocale = (localeString = defaultLocale): CustomLocale => {
    const languageCode = getLanguageCode(localeString);
    const countryCode = getCountryCode(localeString);
    if (!checkLocaleIsValid(localeString)) {
        Sentry.captureException(new Error(`Invalid locale`), {
            extra: {
                languageCode,
                countryCode,
                localeString,
                languages,
                countries,
            },
        });

        return getLocale(defaultLocale);
    }
    const languageLocaleName =
        languages[languageCode].name[languageCode] ?? languages[languageCode].name.en;
    const countryLocaleName =
        countries[countryCode].name[languageCode] ?? countries[countryCode].name.en;

    const language = {
        ...languages[languageCode],
        localeName: languageLocaleName,
    };
    const country = {
        ...countries[countryCode],
        localeName: countryLocaleName,
    };

    return {
        language,
        country,
        languageCode,
        countryCode,
    };
};

export const getSortedLocales = (
    isLoggedIn: boolean,
    i18nLocales: string[] | undefined,
    currentLocale: string,
    localeSortOrder: string[],
): string[] | undefined => {
    const getLocale = (locale: string) => localeDict[locale];
    const localesToShow = isLoggedIn
        ? i18nLocales?.filter((locale) =>
              locale.endsWith(`-${getLocale(currentLocale).country.code}`),
          )
        : i18nLocales;

    const sortLocaleStrings = (localeArray: string[]): string[] => {
        return sortByArray(localeArray, localeSortOrder);
    };

    return localesToShow ? sortLocaleStrings(localesToShow) : undefined;
};
